'use client'

import { useEffect, useState, type CSSProperties } from 'react'
import { hatayiBildir } from '@/lib/hataTakibi/bildir'
import { reactHatasiniCoz } from '@/lib/hataTakibi/reactHataAciklamalari'
import { OPAS_DISPLAY_VERSION } from '@/generated/opasBuildMeta'

/**
 * Ana çerçevenin kendisi çökerse çalışan son durak.
 *
 * Buraya düşüldüğünde tema, yazı tipi ve bileşen kütüphanesi yüklenmemiş olabilir; bu yüzden
 * hiçbir bileşen kullanılmaz, biçim doğrudan yazılır. Aksi hâlde hata sayfası da açılmaz ve
 * kullanıcı bomboş bir ekranla kalır.
 */
export default function GenelHata({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [kopyalandi, setKopyalandi] = useState(false)

  useEffect(() => {
    hatayiBildir(error, 'Uygulama çerçevesi')
  }, [error])

  useEffect(() => {
    if (!kopyalandi) return
    const sayac = window.setTimeout(() => setKopyalandi(false), 2500)
    return () => window.clearTimeout(sayac)
  }, [kopyalandi])

  const sebep = reactHatasiniCoz(error.message)?.gunlukDil ?? null

  const panoyaKopyala = async () => {
    const metin = [
      `OPAS ${OPAS_DISPLAY_VERSION}`,
      `Sayfa: ${window.location.pathname}`,
      `Zaman: ${new Date().toLocaleString('tr-TR')}`,
      error.digest ? `Destek kodu: ${error.digest}` : null,
      `Hata: ${error.message}`,
    ]
      .filter(Boolean)
      .join('\n')

    try {
      await navigator.clipboard.writeText(metin)
      setKopyalandi(true)
    } catch {
      // Pano kapalıysa ikinci bir hata gösterilmez; destek kodu zaten ekranda yazıyor.
    }
  }

  const dugmeTemeli: CSSProperties = {
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }

  return (
    <html lang="tr">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f6f7f9',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          color: '#1f2328',
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: 16,
            border: '1px solid #e3e6ea',
            overflow: 'hidden',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
          }}
        >
          <div style={{ height: 4, backgroundColor: '#C1272D' }} />

          <div style={{ padding: 32, textAlign: 'center' }}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                margin: '0 auto 20px',
                backgroundColor: 'rgba(193, 39, 45, 0.12)',
                color: '#C1272D',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
                fontWeight: 700,
              }}
            >
              !
            </div>

            <h1 style={{ fontSize: 20, margin: '0 0 10px', fontWeight: 700 }}>OPAS açılamadı</h1>

            <p style={{ fontSize: 14, lineHeight: 1.65, color: '#5b6068', margin: 0 }}>
              Beklenmedik bir sorun oluştu ve durum ekibimize bildirildi. Sayfayı yenilediğinizde
              çoğu zaman düzelir.
            </p>

            {sebep && (
              <div
                style={{
                  marginTop: 20,
                  padding: '12px 16px',
                  borderRadius: 10,
                  backgroundColor: '#f2f3f5',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9aa0a6', marginBottom: 2 }}>
                  NE OLDU
                </div>
                <div style={{ fontSize: 13, color: '#5b6068' }}>{sebep}</div>
              </div>
            )}

            {kopyalandi && (
              <div
                style={{
                  marginTop: 20,
                  padding: '10px 16px',
                  borderRadius: 10,
                  backgroundColor: '#e8f5e9',
                  color: '#1b5e20',
                  fontSize: 13,
                  textAlign: 'left',
                }}
              >
                Hata bilgisi panoya kopyalandı. Destek hattına yapıştırabilirsiniz.
              </div>
            )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: 'center',
                marginTop: 24,
              }}
            >
              <button
                type="button"
                onClick={reset}
                style={{ ...dugmeTemeli, backgroundColor: '#C1272D', color: '#ffffff', border: 'none' }}
              >
                Yeniden dene
              </button>
              <button
                type="button"
                onClick={() => window.location.assign('/t-dashboard')}
                style={{
                  ...dugmeTemeli,
                  backgroundColor: 'transparent',
                  color: '#1f2328',
                  border: '1px solid #d5d9de',
                }}
              >
                Ana sayfaya dön
              </button>
              <button
                type="button"
                onClick={() => void panoyaKopyala()}
                style={{
                  ...dugmeTemeli,
                  backgroundColor: 'transparent',
                  color: '#5b6068',
                  border: 'none',
                  fontWeight: 500,
                }}
              >
                Bilgiyi kopyala
              </button>
            </div>

            <p style={{ fontSize: 12, color: '#9aa0a6', margin: '24px 0 0' }}>
              OPAS {OPAS_DISPLAY_VERSION}
              {error.digest ? ` · Destek kodu: ${error.digest}` : ''}
            </p>
          </div>
        </div>
      </body>
    </html>
  )
}
